import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/errors/use-toast';
import { QuestTriggers } from '@/hooks/onboarding/useQuestDetection';

/**
 * Hook for bulk importing research to a project via direct workbook_id assignment
 * UPDATED: Uses direct workbook_id update on workbook_research table
 */
export const useBulkResearchImport = (targetProjectId: string) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (researchIds: string[]) => {
      if (researchIds.length === 0) {
        throw new Error('No research selected for import');
      }

      console.log('[BulkResearchImport] Importing research to project:', {
        targetProjectId,
        researchCount: researchIds.length,
        researchIds
      });

      // Step 1: Get the current user
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        throw new Error('User not authenticated');
      }

      // Step 2: Directly update workbook_id for all selected research
      const { data: updatedResearch, error: updateError } = await supabase
        .from('workbook_research')
        .update({ workbook_id: targetProjectId })
        .in('id', researchIds)
        .eq('user_id', user.id) // Security check
        .select('id, title');

      if (updateError) {
        console.error('[BulkResearchImport] Error updating research assignments:', updateError);
        throw new Error(`Failed to assign research to project: ${updateError.message}`);
      }

      if (!updatedResearch || updatedResearch.length === 0) {
        throw new Error('No research was updated. Please check if you have permission to modify these items.');
      }

      console.log('[BulkResearchImport] Successfully imported research:', {
        projectId: targetProjectId,
        assignedCount: updatedResearch.length,
        data: updatedResearch
      });

      return {
        assigned: updatedResearch,
        assignedCount: updatedResearch.length,
        skippedCount: researchIds.length - updatedResearch.length,
        totalRequested: researchIds.length
      };
    },
    onSuccess: (data, variables) => {
      const { assignedCount, skippedCount, totalRequested } = data;
      
      // Invalidate relevant queries to update the UI
      queryClient.invalidateQueries({ queryKey: ['infinite-user-research'] });
      queryClient.invalidateQueries({ queryKey: ['private-research-generator'] });
      queryClient.invalidateQueries({ queryKey: ['private-research-generator-count'] });
      
      // Invalidate workbook research queries for the project
      queryClient.invalidateQueries({ queryKey: ['workbook-research', targetProjectId] });
      
      // Also invalidate project navigation
      queryClient.invalidateQueries({ queryKey: ['project-navigation', targetProjectId] });
      
      // CRITICAL: Invalidate individual research project assignment queries
      // This updates the "Add to Project" toggles on each research card
      variables.forEach(researchId => {
        queryClient.invalidateQueries({ queryKey: ['research-project-assignment', researchId] });
      });
      
      // Trigger quest completion for research import (Step 7)
      QuestTriggers.researchImported();
      
      // Show appropriate success message
      if (skippedCount > 0) {
        toast({
          title: "Research Import Completed",
          description: `${assignedCount} research item${assignedCount === 1 ? '' : 's'} imported to project. ${skippedCount} item${skippedCount === 1 ? ' was' : 's were'} already assigned.`,
          variant: "default",
        });
      } else {
        toast({
          title: "Research Imported Successfully",
          description: `${assignedCount} research item${assignedCount === 1 ? '' : 's'} imported to project.`,
          variant: "default",
        });
      }
    },
    onError: (error) => {
      console.error('[BulkResearchImport] Error:', error);
      toast({
        title: "Import Failed",
        description: error.message || "Failed to import research to project",
        variant: "destructive",
      });
    },
  });
}; 